<script setup lang="ts">
import {
	Button,
	ProductCardSkeleton,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@laam/ui/base';

import {
	type DropsResponse,
	type ProductsResponseSchema,
	type SuperCarouselData,
	type SuperCarouselItem,
} from '@laam/cms-shared';
import { computed, provide, ref } from 'vue';
import ProductCard from '../../ProductCard.vue';
import SuperDataFetcher from './SuperDataFetcher.vue';
import { useRoute } from 'vue-router';
import { EntityCarouselV2, ProductsCarouselV2 } from '@laam/ui/cms';
import CarouselSkeleton from './CarouselSkeleton.vue';
import EntityCardV2 from './EntityCardV2.vue';
import { PhCaretRight } from '@phosphor-icons/vue';
import EntityCardV3 from './EntityCardV3.vue';

interface Props {
	metadata: SuperCarouselData;
	data: SuperCarouselItem[];
	sectionIndex: number;
}

const { data, metadata } = defineProps<Props>();

const getFormattedPrice = computed(() => {
	return (price: number) => {
		return `${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
	};
});

const route = useRoute();

const identifier = computed(() => route.params.identifier as string);

provide('getFormattedPrice', getFormattedPrice);
provide('currencySymbol', 'PKR');

const selectedTab = ref(data[0]!.title);
const Logan = useLoganStore();

const getTabData = (value: string) => {
	return data.find((item) => item.title === value);
};

const normalizeLAAMUrls = (url: string) => {
	if (url.includes('https://laam.pk')) {
		return url.replace('https://laam.pk', '');
	}
	return url;
};
</script>

<template>
	<div class="gap-3xl lg:gap-7xl p-3xl lg:p-0 md:py-8xl flex flex-col">
		<div class="flex">
			<h2 class="flex grow text-lg font-semibold lg:display-xs text-gray-900">
				{{ metadata.title }}
			</h2>
			<!-- CTA -->
			<template v-if="metadata.cta && metadata.cta.title && metadata.cta.href">
				<NuxtLink :to="normalizeLAAMUrls(metadata.cta.href)">
					<Button variant="tertiary">
						{{ metadata.cta.title }}
						<PhCaretRight />
					</Button>
				</NuxtLink>
			</template>
		</div>

		<div class="entity-x-products-carousel--body gap-3xl flex w-full">
			<Tabs v-model:model-value="selectedTab" class="w-full">
				<TabsList
					class="tabs-list w-fit flex overflow-auto gap-3xl no-scrollbar"
				>
					<h3 v-for="tabItem in data" :key="tabItem.title">
						<TabsTrigger :value="tabItem.title" class="w-fit whitespace-nowrap">
							{{ tabItem.title }}
						</TabsTrigger>
					</h3>
				</TabsList>

				<TabsContent
					v-for="tabItem in data"
					:key="tabItem.title"
					:value="tabItem.title"
					class="lg:mt-7xl mt-3xl"
				>
					<SuperDataFetcher
						v-slot="{ data: output, isLoading }"
						:data="getTabData(selectedTab)!"
						:entity="getTabData(selectedTab)!.entity"
						:identifier="identifier"
						:section-index="sectionIndex"
					>
						<template v-if="tabItem.entity === 'products'">
							<ProductsCarouselV2
								v-if="!isLoading"
								v-slot="{ horizontalIndex, product }"
								:metadata="{
									alignment: 'left',
									entity: 'products',
									input: getTabData(selectedTab)!.input,
									method: 'dynamic',
									title: '',
								}"
								:data="cast<ProductsResponseSchema>(output)"
								:section-index="sectionIndex"
								class="!px-0"
							>
								<ProductCard
									:data="product"
									:index="horizontalIndex"
									:data-source="{
										dataSource: 'valhalla',
										clickedFrom: ['homepage'],
										version: 'v0',
									}"
									:component-info="{
										heading: metadata.title || '',
										component: 'super-products-carousel',
									}"
									page="homepage"
									:horizontal-index="horizontalIndex"
									:vertical-index="sectionIndex"
									:is-hvc="Logan.isHvc"
								/>
							</ProductsCarouselV2>
							<CarouselSkeleton v-else>
								<ProductCardSkeleton />
							</CarouselSkeleton>
						</template>
						<template v-else-if="tabItem.entity === 'drops'">
							<EntityCarouselV2
								v-if="!isLoading"
								v-slot="{ entity, data: entityData, horizontalIndex }"
								:metadata="{
									alignment: 'left',
									entity: tabItem.entity,
									input: getTabData(selectedTab)!.input,
									method: 'dynamic',
									title: '',
								}"
								:data="cast<DropsResponse>(output)"
								:section-index="sectionIndex"
								class="!px-0"
							>
								<EntityCardV2
									:entity="entity"
									:data="entityData"
									:horizontal-index="horizontalIndex"
									:data-source="{
										dataSource: 'valhalla',
										clickedFrom: 'homepage',
										version: '',
									}"
									:component-info="{
										heading: metadata.title || '',
										component: 'super-carousel',
										activeTab: tabItem.title,
										input: tabItem.input,
										method: 'dynamic',
									}"
									:vertical-index="sectionIndex"
									shape="rectangle"
								/>
							</EntityCarouselV2>
							<CarouselSkeleton v-else>
								<ProductCardSkeleton />
							</CarouselSkeleton>
						</template>
						<template v-else-if="tabItem.entity === 'brands'">
							<EntityCarouselV2
								v-if="!isLoading"
								v-slot="{ entity, data: entityData, horizontalIndex }"
								:metadata="{
									alignment: 'left',
									entity: tabItem.entity,
									input: getTabData(selectedTab)!.input,
									method: 'dynamic',
									title: '',
								}"
								:data="cast<DropsResponse>(output)"
								:section-index="sectionIndex"
							>
								<EntityCardV3
									:entity="entity"
									:data="entityData"
									:horizontal-index="horizontalIndex"
									:data-source="{
										dataSource: 'valhalla',
										clickedFrom: 'homepage',
										version: '',
									}"
									:component-info="{
										heading: metadata.title || '',
										component: 'super-carousel',
										activeTab: tabItem.title,
										input: tabItem.input,
										method: 'dynamic',
									}"
									:vertical-index="sectionIndex"
									shape="rectangle"
								/>
							</EntityCarouselV2>
							<CarouselSkeleton v-else>
								<ProductCardSkeleton />
							</CarouselSkeleton>
						</template>
					</SuperDataFetcher>
				</TabsContent>
				<!-- <TabsContent :value="selectedTab">
					<Carousel
						v-if="!isLoading"
						class="drop-tabs--carousel w-full"
						:items-to-show="deviceType === 'mobile' ? 2.2 : 4"
						snap-align="start"
					>
						<Slide
							v-for="(item, horizontalIndex) in page?.products"
							:key="horizontalIndex"
							class="drop-tabs--carousel__item"
							:index="horizontalIndex"
						>
							<div class="mr-3xl lg:mr-7xl w-full overflow-hidden">
								<slot :product="item" :horizontal-index="horizontalIndex">
									<ProductCard
										:data="item"
										:index="horizontalIndex"
										:data-source="{
											dataSource: 'valhalla',
											clickedFrom: ['homepage'],
											version: 'v0',
											parentComponent: 'entity-x-product-carousel',
										}"
										page="homepage"
										:horizontal-index="horizontalIndex"
										:vertical-index="sectionIndex"
										:is-hvc="Logan.isHvc"
									/>
								</slot>
							</div>
						</Slide>

						<template #addons>
							<Navigation
								v-if="
									page && page.products.length > 3 && deviceType !== 'mobile'
								"
							/>
						</template>
					</Carousel>
					
				</TabsContent> -->
			</Tabs>
		</div>

		<!-- <NuxtLink :to="redirectUrl" class="w-full lg:w-fit mx-auto">
			<Button variant="secondary" size="lg" class="w-full lg:w-fit mx-auto">
				<span class="px-xxs">View All</span>
			</Button>
		</NuxtLink> -->
	</div>
</template>

<style>
.drop-tabs--carousel .carousel__prev,
.drop-tabs--carousel .carousel__next {
	@apply bg-white rounded-full p-md mx-3xl lg:p-lg lg:mx-5xl;
}
.drop-tabs--carousel .drop-tabs--carousel__item {
	@apply relative rounded-xs text-xl text-white bg-white w-full;
}

.drop-tabs--carousel .carousel__slide {
	@apply p-none items-start;
}

.drop-tabs--carousel .carousel__pagination {
	@apply absolute mx-auto left-[0px] right-[0px] gap-sm w-fit bottom-3xl;
}

.drop-tabs--carousel .carousel__pagination-item {
	@apply box-border;
}

.drop-tabs--carousel .carousel__pagination-button {
	@apply p-none;
}

.drop-tabs--carousel .carousel__pagination-button::after {
	@apply bg-white rounded-2xl w-10xl h-xs opacity-40;
}

.drop-tabs--carousel .carousel__pagination-button--active::after {
	@apply bg-white opacity-100;
}
</style>
